import { zodResolver } from "@hookform/resolvers/zod";
import { Grid } from "@mui/material";
import { useRef } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";

import { BlFormDropdown } from "Forms/Components/BlFormDropdown";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { useDefaultValues } from "Forms/Hooks/useDefaultValues";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import {
  getModuleTypeOptions,
  getTextLlmOptions,
  getVoiceLlmOptions,
} from "Forms/Utils/EnumOptions";
import {
  type ModuleDetailDto,
  ModuleType,
  type PriceCategoryListItemDto,
  TextLlm,
  VoiceLlm,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  type ModuleFormData,
  moduleFormSchema,
} from "Modules/Forms/moduleFormSchema";
import { RequiredAdornment } from "Shared/Inputs/RequiredAdornment";
import { RichTextEditor } from "Shared/TextEditor/RichTextEditor";

export interface ModuleFormHandle {
  submit: (
    onValid: (data: ModuleFormData) => void | Promise<void>,
  ) => () => void;
  reset: () => void;
}

interface ModuleFormProps {
  module?: ModuleDetailDto | null;
  priceCategories: PriceCategoryListItemDto[];
  isPriceCategoriesLoading?: boolean;
  formRef: React.RefObject<ModuleFormHandle | null>;
  serverError?: Error | null;
}

export const ModuleForm = ({
  module,
  priceCategories,
  isPriceCategoriesLoading,
  formRef,
  serverError,
}: ModuleFormProps): React.ReactNode => {
  const { t } = useResource();

  const internalFormRef = useRef<UseFormReturn<ModuleFormData> | null>(null);

  const defaultValues = useDefaultValues(
    () => buildDefaults(module),
    [module],
    internalFormRef,
  );

  const form = useForm<ModuleFormData>({
    resolver: zodResolver(moduleFormSchema),
    defaultValues,
  });

  internalFormRef.current = form;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = form;

  useHandleServerError({
    form,
    error: serverError,
    formKey: "name",
    resource: Resources.ApiErrors.SaveModule,
  });

  formRef.current = {
    submit: (onValid) => () => handleSubmit(onValid)(),
    reset: () => reset(),
  };

  const priceCategoryOptions = priceCategories.map((pc) => ({
    value: pc.priceCategoryID,
    label: pc.name,
  }));

  return (
    <Grid container spacing={Spacing.M}>
      <Grid size={12}>
        <BlFormInput
          control={control}
          name="name"
          label={t(Resources.Modules.CreateModuleModal.Name)}
          errors={errors}
          placeholder={t(Resources.Modules.CreateModuleModal.NamePlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="type"
          label={t(Resources.Modules.CreateModuleModal.Type)}
          errors={errors}
          options={getModuleTypeOptions()}
          placeholder={t(Resources.Modules.CreateModuleModal.TypePlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="priceCategoryID"
          label={t(Resources.Modules.CreateModuleModal.PriceCategory)}
          errors={errors}
          options={priceCategoryOptions}
          placeholder={t(
            Resources.Modules.CreateModuleModal.PriceCategoryPlaceholder,
          )}
          required
          disabled={isPriceCategoriesLoading}
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="textLlms"
          label={t(Resources.Modules.CreateModuleModal.TextLlmsSection)}
          errors={errors}
          options={getTextLlmOptions()}
          placeholder={t(Resources.Modules.CreateModuleModal.TextLlmsSection)}
          multiple
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="voiceLlms"
          label={t(Resources.Modules.CreateModuleModal.VoiceLlmsSection)}
          errors={errors}
          options={getVoiceLlmOptions()}
          placeholder={t(Resources.Modules.CreateModuleModal.VoiceLlmsSection)}
          multiple
          required
        />
      </Grid>
      <Grid size={12}>
        <RichTextEditor
          control={control}
          name="customPrompt"
          label={t(Resources.Modules.CreateModuleModal.CustomPrompt)}
          errors={errors}
          placeholder={t(
            Resources.Modules.CreateModuleModal.CustomPromptPlaceholder,
          )}
          labelEndAdornment={<RequiredAdornment />}
          onPlainTextChange={(plainText) =>
            setValue("customPromptPlainText", plainText)
          }
        />
      </Grid>
    </Grid>
  );
};

function buildDefaults(module?: ModuleDetailDto | null): ModuleFormData {
  if (!module) {
    return {
      name: "",
      type: ModuleType.SingleVoiceSpot,
      priceCategoryID: "",
      customPrompt: "",
      customPromptPlainText: "",
      textLlms: [TextLlm.OpenAI_ChatGpt5],
      voiceLlms: [VoiceLlm.ElevenLabs_V3],
    };
  }
  return {
    name: module.name,
    type: module.type,
    priceCategoryID: module.priceCategoryID,
    customPrompt: module.customPrompt,
    customPromptPlainText: module.customPromptPlainText,
    textLlms: module.textLlms,
    voiceLlms: module.voiceLlms,
  };
}
